import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Description, FlexSpan, IconButton, Switch } from "@evergis/ui";
import { generateId } from "@evergis/api";
import { Flex } from "grid-styled";
import { isNil } from "lodash";

import {
  MainContainer,
  Content,
  ActionsWrapper,
  ActionsSwitcher,
  ActionsContainer,
  ActionsDivider,
  FilterConditionsWrapper,
  LayerFilterContainer,
  Header,
  Title,
  SubTitle,
  AndConditionTitle,
  OrConditionTitle,
} from "./styled";

import { ConditionEditor } from "./components/ConditionEditor";
import { FeaturesFilterConditions } from "./components/FeaturesFilterConditions";
import { FeaturesFilterCondition } from "./components/FeaturesFilterCondition";
import { ClearConditionsButton } from "./components/ClearConditionsButton";
import { AddConditionButton } from "./components/AddConditionButton";
import { EqlParams } from "./components/EqlParams";

import { useFilterDevMode } from "../../hooks/appConfig/useFilterDevMode";
import { useLayerCondition } from "./hooks/useFilterConditions";
import { useValidationError } from "../../hooks/useValidationError";
import { useDevMode } from "./hooks/useDevMode";
import { useLayerFilterUpdate } from "./hooks/useLayerFilterUpdate";
import { isConditionSpecialFunc } from "../../utils/isConditionSpecialFunc";
import { checkConditionUnfilled, DevModeEffects } from "./hooks/DevModeEffects";
import { isObject } from "../../utils/isObject";
import { useTempFilter } from "../../hooks/filter/useTempFilter";
import { useLayerTempFilter } from "./hooks/useLayerTempFilter";
import { setLayerTempFilterTool } from "../../ducks/layerTempFilter/layerTempFilter";
import { usePostgresLayer } from "../../hooks/layer/usePostgresLayer";
import { getLayerTempFilterTool } from "../../ducks/layerTempFilter/selectors";
import { useLayerParams } from "../../hooks/eql/useLayerParams";
import { useValidationServerError } from "../../hooks/useValidationServerError";
import { isConfigure } from "../../utils/permissions";

import { FilterConditionOperation, FilterConditionType } from "./types";

// eslint-disable-next-line max-lines-per-function
export const LayerFilter: FC = () => {
  const { t } = useTranslation("layerFilter");
  const dispatch = useDispatch();
  const { layerName } = useSelector(getLayerTempFilterTool);
  const { postgresLayer: layer } = usePostgresLayer(layerName, { waitInput: true });
  const [, setTempFilterId] = useTempFilter();
  const [{ conditions, rawCondition, conditionOperation, parsingError }, update] = useLayerCondition(layer);
  const [devMode, toggleDevMode] = useDevMode(parsingError);
  const devModeSwitchEnabled = useFilterDevMode();
  const { error: validationError } = useValidationError();
  const { setError } = useValidationServerError();
  const someConditionsUnfilled = useMemo(() => conditions?.some(checkConditionUnfilled), [conditions]);
  const [layerParams, updateLayerParams] = useLayerParams(layer);
  const [initialLayerParams, setInitialLayerParams] = useState<Record<string, string> | undefined>();
  const [tempLayerParams, setTempLayerParams] = useState<Record<string, string> | undefined>();
  const isLayerConfigurable = useMemo(() => isConfigure(layer?.permissions), [layer]);

  const {
    filter: onApply,
    loading: isApplying,
    serverCondition,
  } = useLayerFilterUpdate(layer, rawCondition, conditions, conditionOperation, {
    devMode,
  });

  const isActionDisabled = someConditionsUnfilled || !!validationError;
  const isSpecialFunc = isConditionSpecialFunc(rawCondition);

  const isComplex = useMemo(() => {
    return (
      isSpecialFunc ||
      conditions?.filter(condition => isObject(condition.attribute) || isObject(condition.value)).length > 0
    );
  }, [conditions, isSpecialFunc]);

  const isAndConditionOperation = useMemo(
    () => conditionOperation === FilterConditionOperation.AND,
    [conditionOperation],
  );

  const onAddNewCondition = useCallback(
    (condition: FilterConditionType) => update.addCondition({ ...condition, id: generateId() }),
    [update],
  );

  const onClearConditions = useCallback(() => {
    update.setConditions([]);
    update.setRawCondition("");
  }, [update]);

  const setAndConditionOp = useCallback(
    () => !isAndConditionOperation && update.toggleConditionOperation(),
    [isAndConditionOperation, update],
  );

  const setOrConditionOp = useCallback(
    () => isAndConditionOperation && update.toggleConditionOperation(),
    [isAndConditionOperation, update],
  );

  const resetLayerFilter = useCallback(() => {
    dispatch(setLayerTempFilterTool({}));
    setTempFilterId(undefined);
  }, [dispatch, setTempFilterId, initialLayerParams]); // eslint-disable-line

  const onApplyFilter = useCallback(async () => {
    if (layer.type === "QueryLayerService") {
      await updateLayerParams({
        input: {
          layerName: layer.name,
          payload: tempLayerParams,
        },
      });
    }

    if (isLayerConfigurable) {
      const result = await onApply();

      if (result && !result.isValid) {
        setError(result);
      } else {
        resetLayerFilter();
      }
    } else {
      resetLayerFilter();
    }
  }, [layer, tempLayerParams, resetLayerFilter, isLayerConfigurable, onApply]); // eslint-disable-line

  DevModeEffects.useConditions(devMode === true && !parsingError, conditions, conditionOperation, update, rawCondition);
  DevModeEffects.useRawCondition(devMode === false, rawCondition, update);

  useLayerTempFilter(layer, serverCondition, !!validationError);

  useEffect(() => {
    if (layerParams) {
      const processedLayerParams = Object.entries(layerParams).reduce((acc, [key, value]) => {
        return {
          ...acc,
          [key]: !isNil(value) ? value : "",
        };
      }, {});

      if (initialLayerParams === undefined) {
        setInitialLayerParams(processedLayerParams);
      }

      setTempLayerParams(processedLayerParams);
    }
  }, [layerParams]); // eslint-disable-line

  return (
    <>
      <MainContainer>
        <Content>
          <Title>{layer?.alias || layer?.name}</Title>
          <ActionsWrapper>
            <ActionsSwitcher>
              <Description>{t("filtering")}</Description>
            </ActionsSwitcher>
            <ActionsContainer>
              <IconButton
                kind="close"
                onClick={async () => {
                  if (layer.type === "QueryLayerService") {
                    await updateLayerParams({
                      input: {
                        layerName: layer.name,
                        payload: initialLayerParams,
                      },
                    });
                  }

                  resetLayerFilter();
                }}
                error
              />
              <IconButton kind="success" onClick={onApplyFilter} primary disabled={isActionDisabled || isApplying} />
              <ActionsDivider />
              <ClearConditionsButton clearConditions={onClearConditions} disabled={(conditions?.length ?? 0) === 0} />
            </ActionsContainer>
          </ActionsWrapper>
        </Content>
      </MainContainer>
      {layer.type === "QueryLayerService" && (
        <EqlParams layer={layer} tempParams={tempLayerParams} setTempParams={setTempLayerParams} />
      )}
      {isLayerConfigurable && (
        <>
          {!devMode ? (
            <LayerFilterContainer>
              {!isComplex && (
                <Header>
                  <SubTitle>
                    <Flex alignItems="center">
                      <FlexSpan style={{ position: "relative", top: "-1px" }}>{t("attributes")}</FlexSpan>
                      {isLayerConfigurable && devModeSwitchEnabled && (
                        <FlexSpan>
                          <IconButton kind="switcher" primary={devMode} onClick={toggleDevMode} />
                        </FlexSpan>
                      )}
                    </Flex>
                  </SubTitle>
                  {isLayerConfigurable && (
                    <div>
                      <Flex flexDirection={"row"} alignItems={"center"}>
                        <AndConditionTitle checked={isAndConditionOperation} onClick={setAndConditionOp}>
                          {t("all")}
                        </AndConditionTitle>
                        <Switch checked={!isAndConditionOperation} isOn onChange={update.toggleConditionOperation} />
                        <OrConditionTitle checked={!isAndConditionOperation} onClick={setOrConditionOp}>
                          {t("atLeastOne")}
                        </OrConditionTitle>
                      </Flex>
                    </div>
                  )}
                </Header>
              )}
              <FilterConditionsWrapper>
                <FeaturesFilterConditions
                  isComplex={isComplex}
                  conditions={conditions}
                  clearConditions={onClearConditions}
                  toggleDevMode={toggleDevMode}
                >
                  {filterCondition => (
                    <FeaturesFilterCondition
                      key={filterCondition.id}
                      layer={layer}
                      condition={filterCondition}
                      updateCondition={update.updateCondition}
                      deleteCondition={update.deleteCondition}
                    />
                  )}
                </FeaturesFilterConditions>
                {!isComplex && <AddConditionButton addCondition={onAddNewCondition} />}
              </FilterConditionsWrapper>
            </LayerFilterContainer>
          ) : (
            <>
              <Header style={{ height: "auto", paddingTop: "1.2rem" }}>
                <SubTitle>
                  <Flex alignItems="center">
                    <FlexSpan style={{ position: "relative", top: "-1px" }}>{t("attributes")}</FlexSpan>
                    <FlexSpan>
                      <IconButton kind="switcher" primary={devMode} onClick={toggleDevMode} />
                    </FlexSpan>
                  </Flex>
                </SubTitle>
              </Header>
              <ConditionEditor layerName={layer?.name} condition={rawCondition} setCondition={update.setRawCondition} />
            </>
          )}
        </>
      )}
    </>
  );
};
